import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Paper, Select, type SelectChangeEvent, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { apiFetch } from '../api/client';
import { useBusiness } from '../contexts/BusinessContext';

type SubmissionRow = { submission_id: string; config_id: string; session_id?: string; status: string; created_at?: string };
type ConfigOpt = { id: string; name: string };

export default function AdminSubmissionsPage() {
  const { activeTenantId, activeEntityId, activeConfigId, setActiveConfigId } = useBusiness();
  const [rows, setRows] = useState<SubmissionRow[]>([]);
  const [configs, setConfigs] = useState<ConfigOpt[]>([]);
  const [formConfigId, setFormConfigId] = useState<string>('');
  const [sessionId, setSessionId] = useState('manual-session-1');
  const [error, setError] = useState('');

  const loadConfigs = async () => {
    if (!activeTenantId) return;
    const resp = await apiFetch('/api/form-configs');
    if (!resp.ok) return;
    const data = (await resp.json()) as Array<{ id: string; name: string }>;
    setConfigs(Array.isArray(data) ? data.map((c) => ({ id: c.id, name: c.name })) : []);
  };

  const load = async () => {
    if (!activeTenantId) return;
    const qs = new URLSearchParams({ tenant_id: activeTenantId });
    if (formConfigId) qs.set('config_id', formConfigId);
    const resp = await apiFetch(`/api/form-submissions?${qs.toString()}`);
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    setRows((await resp.json()) as SubmissionRow[]);
  };

  useEffect(() => {
    void loadConfigs();
  }, [activeTenantId]);

  // Pick a valid config id for the dropdown (avoids MUI Select out-of-range value when context id is not in list).
  useEffect(() => {
    if (!configs.length) {
      setFormConfigId('');
      setActiveConfigId(null);
      return;
    }
    const ids = new Set(configs.map((c) => c.id));
    if (formConfigId && ids.has(formConfigId)) return;
    if (activeConfigId && ids.has(activeConfigId)) {
      setFormConfigId(activeConfigId);
      return;
    }
    const nextId = configs[0].id;
    setFormConfigId(nextId);
    setActiveConfigId(nextId);
  }, [configs, activeTenantId, activeConfigId, formConfigId, setActiveConfigId]);

  useEffect(() => {
    void load();
  }, [activeTenantId, formConfigId]);

  const onConfigChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    setFormConfigId(v);
    setActiveConfigId(v || null);
  };

  const create = async () => {
    if (!activeTenantId || !formConfigId) {
      setError('Select a tenant (header) and a form config.');
      return;
    }
    const resp = await apiFetch('/api/form-submissions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenant_id: activeTenantId,
        entity_id: activeEntityId || null,
        config_id: formConfigId,
        session_id: sessionId,
        status: 'draft',
        // data_json: { demo: { field: 'value', timestamp: new Date().toISOString() } },
        data_json: {},
      }),
    });
    if (!resp.ok) {
      setError(await resp.text());
      return;
    }
    setError('');
    try { localStorage.setItem('admin_phase1_submission_done', '1'); } catch {}
    await load();
  };

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e6ebf1' }}>
      <Typography variant="h6" fontWeight={700} sx={{ color: '#012676' }} gutterBottom>
        Form Submissions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose the form config for new submissions. The tenant in the app header must match that config&apos;s tenant.
      </Typography>
      {!activeTenantId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select tenant and entity first before creating submissions.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'flex-start' }}>
        <FormControl size="small" sx={{ minWidth: 300 }} required>
          <InputLabel id="submission-config-label">Form config</InputLabel>
          <Select<string>
            labelId="submission-config-label"
            label="Form config"
            value={formConfigId}
            onChange={onConfigChange}
            disabled={!activeTenantId}
          >
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Session ID"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          sx={{ minWidth: 220 }}
          disabled={!activeTenantId}
        />
        <Box sx={{ pt: 0.5 }}>
          <Button variant="contained" onClick={create} disabled={!formConfigId}>
            Create Submission
          </Button>
        </Box>
        <Box sx={{ pt: 0.5 }}>
          <Button variant="outlined" onClick={() => void load()}>
            Refresh
          </Button>
        </Box>
      </Stack>
      {configs.length === 0 && activeTenantId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No form configs for this tenant. Create one under Configs first.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Config</TableCell>
            <TableCell>Session</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.submission_id}>
              <TableCell>{r.submission_id}</TableCell>
              <TableCell>{r.config_id}</TableCell>
              <TableCell>{r.session_id || '-'}</TableCell>
              <TableCell>{r.status}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
